/* eslint-disable no-bitwise */
import resolveColor from "@insite/mobius/utilities/resolveColor";
import { css } from "styled-components";

export default function getColor(path = "") {
    return (props: any) => resolveColor(path, props.theme);
}

function adjustColor(hex: string, percent: number) {
    const num = parseInt(hex.replace("#", ""), 16);
    let r = (num >> 16) + percent;
    let g = ((num >> 8) & 0x00ff) + percent;
    let b = (num & 0x0000ff) + percent;
    r = Math.min(255, Math.max(0, r));
    g = Math.min(255, Math.max(0, g));
    b = Math.min(255, Math.max(0, b));
    return `#${((1 << 24) | (r << 16) | (g << 8) | b).toString(16).slice(1)}`;
}

const CSS_HEX_REGEX = /^#([A-Fa-f0-9]{3,8})$/;
const CSS_RGB_A_REGEX = /^rgba?\(/i;
const CSS_HSL_A_REGEX = /^hsla?\(/i;

function looksLikeCssColor(s: string) {
    if (!s || typeof s !== "string") {
        return false;
    }
    const trimmed = s.trim();
    if (CSS_HEX_REGEX.test(trimmed)) {
        return true;
    }
    if (CSS_RGB_A_REGEX.test(trimmed)) {
        return true;
    }
    if (CSS_HSL_A_REGEX.test(trimmed)) {
        return true;
    }
    if (/^[a-zA-Z]+$/.test(trimmed)) {
        return true;
    }
    return false;
}

export function getButtonColor(variant: string) {
    return (props: any) => {
        const theme = props?.theme || {};
        const btn = (theme.button as any)?.[variant];

        if (btn && btn.backgroundColor !== undefined && btn.backgroundColor !== null && btn.backgroundColor !== "") {
            const raw = btn.backgroundColor;
            if (typeof raw === "string" && !looksLikeCssColor(raw)) {
                const resolved = getColor(raw)(props);
                if (resolved) {
                    return resolved;
                }
            }
            return raw;
        }

        const btnColor = btn?.color;
        if (typeof btnColor === "string") {
            if (looksLikeCssColor(btnColor)) {
                return btnColor;
            }

            if (btnColor.includes(".")) {
                const resolved = getColor(btnColor)(props);
                if (resolved) {
                    return resolved;
                }
            } else {
                const try1 = getColor(`colors.${btnColor}.main`)(props);
                if (try1) {
                    return try1;
                }
                const try2 = getColor(`${btnColor}.main`)(props);
                if (try2) {
                    return try2;
                }
            }
        }

        const tryVariant1 = getColor(`colors.${variant}.main`)(props);
        if (tryVariant1) {
            return tryVariant1;
        }
        const tryVariant2 = getColor(`${variant}.main`)(props);
        if (tryVariant2) {
            return tryVariant2;
        }

        // fallback
        return "#000000";
    };
}

export function getButtonHover(variant: string, amount = 20) {
    const getBase = getButtonColor(variant);
    return (props: any) => {
        const theme = props?.theme || {};
        const btn = (theme.button as any)?.[variant];
        if (btn?.hoverBackgroundColor) {
            return btn.hoverBackgroundColor;
        }
        const base = getBase(props) || "#000000";
        const mode = (btn?.hoverMode || "").toString().toLowerCase();
        if (mode === "darken") {
            return adjustColor(base, -amount);
        }
        if (mode === "lighten") {
            return adjustColor(base, amount);
        }
        return base;
    };
}

export function getTypography(theme: any, key: string) {
    const t = theme?.typography?.[key] || {};
    return {
        color: t.color ?? theme?.colors?.text?.main ?? "#000",
        size: typeof t.size === "number" ? `${t.size}px` : (t.size ?? undefined),
        weight: t.weight ?? undefined,
        lineHeight: t.lineHeight ?? undefined,
        css: t.css ?? "",
        fontFamily: t.fontFamily ?? theme?.typography?.body?.fontFamily ?? "inherit",
    };
}

export const applyTypography = (tag: string) => (p: any) => {
    const t = (p.theme as any)?.typography?.[tag] || {};
    return css`
        color: ${t.color
            ? getColor(t.color)(p) // resolves "info.main" to actual color
            : ((p.theme as any)?.colors?.text?.main ?? "#000")};
        font-family: ${t.fontFamily ?? (p.theme as any)?.typography?.body?.fontFamily ?? "inherit"};
        font-weight: ${t.weight ?? 400};
        ${t.size ? `font-size: ${t.size};` : ""}
        ${t.lineHeight ? `line-height: ${t.lineHeight};` : ""}
        ${t.letterSpacing ? `letter-spacing: ${t.letterSpacing};` : ""}
        ${t.transform ? `text-transform: ${t.transform};` : ""}
        ${t.underline ? "text-decoration: underline;" : ""}
        ${t.italic ? "font-style: italic;" : ""}
        ${t.css ? (typeof t.css === "string" ? t.css : t.css) : ""}
    `;
};